import React, { useCallback, useState } from 'react'
import { useDebouncedValue } from 'hooks/useDebouncedValue'

import dayjs from 'dayjs'
import { Stack, Typography } from '@mui/material'

import { Activity } from 'components'
import { MultiSelectFilter, DateRangePickerFilter } from 'components/input/filters'
import { ActivityDownload, ExportFormat } from 'components/input/ActivityDownload/ActivityDownload'
import { useCompanyActivityInfinite } from 'hooks/api'
import { ActivityItem, companyActivityApi } from 'services/companyActivityApi'
import type { ActivityRow } from 'components/display/Activity/Activity'
import { ROLES } from 'constants/roles'
import { exportRowsToCsv, exportRowsToXlsx } from 'utils/exportActivity'
import {
  Building2, UserPlus, UserCog, UserMinus, ShieldCheck, ShieldPlus, Shield,
  ShieldMinus, Settings2, CreditCard, Activity as ActivityIcon,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Role filter — one chip per unique display label. Selecting a label adds ALL
// of its underlying DB role names to the ?role= query param so both
// company_admin + platform_admin fire when the user picks "Admin".
// TODO(VERA-955 / VERA-780): add custom role names when the backend supports them.
// ---------------------------------------------------------------------------
const ROLE_LABEL_GROUPS: { label: string; names: string[] }[] = [
  { label: 'Admin',   names: ['company_admin', 'platform_admin'] },
  { label: 'Member',  names: ['company_team_member'] },
  { label: 'Manager', names: ['platform_manager'] },
]

/** Unique display labels — these are the chip options. */
const ROLE_LABEL_OPTIONS = ROLE_LABEL_GROUPS.map(g => g.label)

/** Map a selected label → its raw DB role names (for ?role= param). */
function labelToNames(label: string): string[] {
  return ROLE_LABEL_GROUPS.find(g => g.label === label)?.names ?? [label]
}

/** Map raw DB role names back → selected labels (for chip selected-state). */
function namesToLabels(names: string[]): string[] {
  return ROLE_LABEL_GROUPS
    .filter(g => g.names.some(n => names.includes(n)))
    .map(g => g.label)
}

// ---------------------------------------------------------------------------
// Helpers: map endpoint ActivityItem → Activity component row shape
// ---------------------------------------------------------------------------

type ActionVerb = 'updated' | 'added' | 'removed'

const ACTION_VERB_MAP: Record<string, ActionVerb> = {
  company_updated: 'updated',
  user_updated: 'updated',
  permissions_updated: 'updated',
  activation_settings_updated: 'updated',
  payment_method_updated: 'updated',
  user_added: 'added',
  role_added: 'added',
  user_removed: 'removed',
  role_removed: 'removed',
  role_updated: 'updated',
}

// Category icon per action type (shown in the timeline gutter). Unmapped actions
// fall back to a generic activity glyph so the feed never looks half-done.
const ACTION_ICON_MAP: Record<string, React.ReactNode> = {
  company_updated: <Building2 />,
  user_added: <UserPlus />,
  user_updated: <UserCog />,
  user_removed: <UserMinus />,
  permissions_updated: <ShieldCheck />,
  role_added: <ShieldPlus />,
  role_updated: <Shield />,
  role_removed: <ShieldMinus />,
  activation_settings_updated: <Settings2 />,
  payment_method_updated: <CreditCard />,
}

function iconForAction(action: string): React.ReactNode {
  return ACTION_ICON_MAP[action] ?? <ActivityIcon />
}

function mapActionLabel(item: ActivityItem): string {
  const verb = ACTION_VERB_MAP[item.action] ?? 'updated'
  if (item.target_label) {
    return `${verb} ${item.target_label}`
  }
  return verb
}

/** Compact string representation of a before/after value. Shared by the
 * timeline renderer and the CSV/XLSX export mapper. */
function stringify(val: unknown): string {
  if (val == null) return ''
  if (typeof val === 'string') return val
  if (typeof val === 'object') {
    // Render a compact key=value summary for objects
    const entries = Object.entries(val as Record<string, unknown>)
      .filter(([, v]) => v != null && v !== '')
      .slice(0, 3)
      .map(([k, v]) => `${k}: ${v}`)
      .join(', ')
    return entries
  }
  return String(val)
}

/**
 * `result` is rendered through an HTML parser, so anything interpolated into it must be
 * escaped. These values are brand-authored free text (company names, addresses, role labels)
 * reaching the string directly — this was interpolating them raw.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * What a value LOOKS like in the row, versus what it actually is.
 *
 * A logo change is the case that forced this: `before` and `after` are both full GCS URLs
 * sharing a ~90-character prefix, so the row rendered two near-identical strings, differing
 * only in the final extension, and measured ~1900px wide. End-truncation is no help — the
 * distinguishing part of a URL is at the END, so clipping the tail removes the only
 * information in it and leaves two identical-looking values.
 *
 * So a URL renders as its filename ("logo.jpg → logo.png"), with the whole thing on the title
 * attribute for anyone who needs the address. Non-URL values middle-truncate, which keeps both
 * ends for things like IDs.
 */
const URL_RE = /^https?:\/\//i

function displayValue(raw: string): { text: string; title?: string } {
  if (URL_RE.test(raw)) {
    try {
      const segments = new URL(raw).pathname.split('/').filter(Boolean)
      const last = segments[segments.length - 1]
      if (last) return { text: decodeURIComponent(last), title: raw }
    } catch {
      // Not parseable as a URL after all — fall through to length handling.
    }
  }
  if (raw.length > 80) {
    return { text: `${raw.slice(0, 40)}…${raw.slice(-24)}`, title: raw }
  }
  return { text: raw }
}

function valueSpan(raw: string): string {
  const { text, title } = displayValue(raw)
  const titleAttr = title ? ` title="${escapeHtml(title)}"` : ''
  return `<span${titleAttr}>${escapeHtml(text)}</span>`
}

function mapResult(item: ActivityItem): string {
  const { before, after } = item
  if (before == null && after == null) return ''

  const beforeStr = stringify(before)
  const afterStr = stringify(after)

  if (beforeStr && afterStr) {
    return `${valueSpan(beforeStr)} &rarr; ${valueSpan(afterStr)}`
  }
  if (afterStr) return valueSpan(afterStr)
  if (beforeStr) return valueSpan(beforeStr)
  return ''
}

function mapItemToRow(item: ActivityItem): ActivityRow {
  const actor = item.actor ?? {
    id: '',
    full_name: 'System',
    avatar: null,
    role: { name: '' },
  }

  return {
    user: {
      id: actor.id,
      // `full_name` is nullable and, in practice, often null — a seeded or invited admin who
      // never filled in a profile. Left raw it rendered an EMPTY name beside a fallback
      // person-glyph avatar, so the row read as "‹blank circle› ‹nothing› Admin updated Logo"
      // and looked broken rather than anonymous. Same phrasing the certificate feed already
      // uses for the same situation (`adminName` in certificateActivityRows).
      full_name: actor.full_name || 'A teammate',
      avatar: actor.avatar,
      // name = raw DB role name (used for routing); displayName = human label.
      role: actor.role ? { name: actor.role.name, displayName: ROLES[actor.role.name] ?? actor.role.name } : null,
    },
    action: mapActionLabel(item),
    result: mapResult(item),
    date: item.created_at,
    icon: iconForAction(item.action),
  }
}

// ---------------------------------------------------------------------------
// Columns definition — filterable: false suppresses per-column search inputs
// in the Filters drawer while sortable: true keeps them in the Sort popover.
// ---------------------------------------------------------------------------
const COMPANY_COLUMNS = [
  { field: 'user.full_name', headerName: 'Actor', type: 'string', sortable: true, filterable: false },
  { field: 'user.role.name', headerName: 'Role', type: 'string', sortable: true, filterable: false },
  { field: 'action', headerName: 'Action', type: 'string', sortable: true, filterable: false },
  { field: 'date', headerName: 'Date', type: 'string', sortable: true, filterable: false },
]

// Personal (actor-fixed) scope: every row shares the same actor, so "Actor" and
// "Role" earn no place as sortable columns — sorting by a value that never
// varies is a no-op control that only adds noise to the Sort popover.
const PERSONAL_COLUMNS = [
  { field: 'action', headerName: 'Action', type: 'string', sortable: true, filterable: false },
  { field: 'date', headerName: 'Date', type: 'string', sortable: true, filterable: false },
]

export interface ActivityLogBodyProps {
  /**
   * Pin the feed to one actor's own actions ("just mine") instead of the whole
   * company's — the Personal → Activity page passes the signed-in user's id here.
   *
   * NOT user-editable: there is no control anywhere that lets a viewer change this
   * value. When set, the role filter and the Actor/Role columns are also dropped
   * (every row already shares the same actor, so filtering/sorting by it is noise —
   * see PERSONAL_COLUMNS above), and copy + the export filename switch to the
   * personal phrasing.
   */
  fixedActorId?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const ActivityLogBody = ({ fixedActorId }: ActivityLogBodyProps) => {
  const isPersonalScope = !!fixedActorId

  // Server-side filter state
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [dateFrom, setDateFrom] = useState<string | undefined>(undefined)
  const [dateTo, setDateTo] = useState<string | undefined>(undefined)
  const [sortParam, setSortParam] = useState<string>('created_at:desc')
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
  }, [])

  // Server-side filter params (page/pageSize are owned by the infinite hook). This
  // object is the query key, so changing any filter resets the list to page 1.
  // fixedActorId is pinned here rather than exposed as UI state — the personal page
  // has no control that can change it.
  const filterParams = {
    ...(fixedActorId ? { actorUserId: fixedActorId } : {}),
    ...(!isPersonalScope && selectedRoles.length > 0 ? { role: selectedRoles.join(',') } : {}),
    ...(dateFrom ? { from: dateFrom } : {}),
    ...(dateTo ? { to: dateTo } : {}),
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    sort: sortParam,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCompanyActivityInfinite(filterParams)

  // Flatten the loaded pages into one row list (infinite scroll appends pages).
  const items = (data?.pages ?? []).flatMap((p) => p.items)

  const rows: ActivityRow[] = items.map(mapItemToRow)

  // ---------------------------------------------------------------------------
  // Export handler — fetches ALL matching rows for current filters (up to 5000)
  // and downloads as CSV or XLSX.
  // TODO(VERA-955): very large logs would need server-side streaming/pagination.
  // ---------------------------------------------------------------------------
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = useCallback(async (format: ExportFormat) => {
    setIsExporting(true)
    try {
      const baseParams = {
        ...(fixedActorId ? { actorUserId: fixedActorId } : {}),
        ...(!isPersonalScope && selectedRoles.length > 0 ? { role: selectedRoles.join(',') } : {}),
        ...(dateFrom ? { from: dateFrom } : {}),
        ...(dateTo ? { to: dateTo } : {}),
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        sort: sortParam,
      }
      // The read endpoint caps pageSize at 100, so page through it to collect all
      // matching rows (bounded to avoid a runaway on very large logs).
      // TODO(VERA-955): a server-side streaming export would beat client pagination for huge logs.
      const EXPORT_PAGE_SIZE = 100
      const MAX_EXPORT_ROWS = 5000
      const collected: ActivityItem[] = []
      for (let pageNum = 1; collected.length < MAX_EXPORT_ROWS; pageNum++) {
        const res = await companyActivityApi.getCompanyActivity({ ...baseParams, page: pageNum, pageSize: EXPORT_PAGE_SIZE })
        collected.push(...res.items)
        if (res.items.length < EXPORT_PAGE_SIZE || collected.length >= res.total) break
      }
      const exportRows = collected.slice(0, MAX_EXPORT_ROWS).map((item) => {
        const actor = item.actor
        return {
          Actor: actor?.full_name ?? 'System',
          Role: actor?.role ? (ROLES[actor.role.name] ?? actor.role.name) : '',
          // mapActionLabel already includes the target label — don't append it again.
          Action: mapActionLabel(item),
          Before: stringify(item.before),
          After: stringify(item.after),
          Date: new Date(item.created_at).toLocaleString(),
        }
      })
      const filenameBase = `${isPersonalScope ? 'my' : 'company'}-activity-${new Date().toISOString().slice(0, 10)}`
      if (format === 'csv') {
        exportRowsToCsv(exportRows, filenameBase)
      } else {
        await exportRowsToXlsx(exportRows, filenameBase)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Activity export failed', e)
    } finally {
      setIsExporting(false)
    }
  }, [fixedActorId, isPersonalScope, selectedRoles, dateFrom, dateTo, debouncedSearch, sortParam])

  // ---------------------------------------------------------------------------
  // Sort handler — maps Activity component sort format → server sort param.
  // The Activity component's internal sort state is still updated (Sort popover
  // reflects selection); this callback additionally fires the server-side param.
  // ---------------------------------------------------------------------------
  const handleSortChange = useCallback((col: any, direction?: string, override?: boolean) => {
    // Array = full override from the Sort popover (multi-sort)
    if (Array.isArray(col)) {
      if (col.length === 0) {
        setSortParam('created_at:desc')
      } else {
        const parts = col
          .map((s: { field: string; sort: string }) => {
            // Map component field names to endpoint field names
            const fieldMap: Record<string, string> = {
              date: 'created_at',
              action: 'action',
            }
            const serverField = fieldMap[s.field] ?? 'created_at'
            return `${serverField}:${s.sort ?? 'asc'}`
          })
          .join(',')
        setSortParam(parts)
      }
      return
    }
    if (col === 'all' && direction === 'clear') {
      setSortParam('created_at:desc')
      return
    }
    // Single sort change
    const fieldMap: Record<string, string> = {
      date: 'created_at',
      action: 'action',
    }
    const serverField = fieldMap[col] ?? 'created_at'
    setSortParam(`${serverField}:${direction ?? 'asc'}`)
  }, [])

  // ---------------------------------------------------------------------------
  // Role filter handler — receives selected display LABELS from MultiSelectFilter
  // (since roleColumn.options = ROLE_LABEL_OPTIONS). Expands each label to all
  // underlying DB role names before sending to the server ?role= param.
  // Actor (user) filter: TODO(VERA-955) — server-side actor filter requires a
  // user picker backed by a separate user-list query; wired here as client-side
  // search (the Activity component's built-in search box covers it).
  // Company scope only — the personal page pins the actor and never renders this.
  // ---------------------------------------------------------------------------
  const handleRoleFilterChange = useCallback((filterObj: { field: string; operator: string; value: string[] }) => {
    const labels = filterObj.value ?? []
    const names = labels.flatMap(labelToNames)
    setSelectedRoles(names)
  }, [])

  // ---------------------------------------------------------------------------
  // Date filter handler — receives two calls from DateRangePickerFilter
  // (onOrAfter + onOrBefore). We coerce them to ISO strings for the server.
  // ---------------------------------------------------------------------------
  const handleDateFilterChange = useCallback((filterObj: { field: string; operator: string; value: Date }) => {
    if (filterObj.operator === 'onOrAfter') {
      setDateFrom(filterObj.value ? dayjs(filterObj.value).toISOString() : undefined)
    } else if (filterObj.operator === 'onOrBefore') {
      setDateTo(filterObj.value ? dayjs(filterObj.value).toISOString() : undefined)
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Extra filter controls rendered inside the Filters drawer.
  // roleColumn.options = unique display labels (Admin / Member / Manager).
  // MultiSelectFilter stores + emits selected labels; handleRoleFilterChange
  // expands them to raw DB names for the ?role= query param.
  //
  // Personal scope drops the role filter entirely: every row already shares the
  // signed-in user's role, so filtering by it can only ever produce "all rows" or
  // "no rows" — a control with no useful effect is noise, not a feature.
  // ---------------------------------------------------------------------------
  const roleColumn = {
    field: 'role',
    headerName: 'Role',
    options: ROLE_LABEL_OPTIONS,
    renderCell: ({ value }: { value: string }) => (
      <Typography variant="body2">{value}</Typography>
    ),
  }

  // Derive currently-selected labels from raw DB names so the chips reflect
  // the current server filter state (e.g. after a page reload or URL param).
  const selectedRoleLabels = namesToLabels(selectedRoles)

  const dateColumn = {
    field: 'date',
    headerName: 'Activity date',
  }

  // extraFiltersDirty: show Apply as active whenever any server filter differs
  // from the "no filter" baseline (the query already refetches on state change,
  // but we still light up the Apply button so users know filters are pending).
  const extraFiltersDirty = (!isPersonalScope && selectedRoles.length > 0) || !!dateFrom || !!dateTo

  const extraFilters = (
    <>
      {!isPersonalScope && (
        <MultiSelectFilter
          column={roleColumn}
          rows={[]}
          filter={selectedRoleLabels}
          onChange={handleRoleFilterChange}
        />
      )}
      <DateRangePickerFilter
        column={dateColumn}
        rows={[]}
        filters={[
          ...(dateFrom ? [{ operator: 'onOrAfter', value: dateFrom }] : []),
          ...(dateTo ? [{ operator: 'onOrBefore', value: dateTo }] : []),
        ]}
        onChange={handleDateFilterChange}
      />
    </>
  )

  return (
    <Stack>
      {/* On the personal feed every row is the same person, so the actor block is a
          constant — dropped there, kept in full on the company feed. See the prop's docblock. */}
      <Activity
        actor={isPersonalScope ? 'none' : 'full'}
        activity={rows}
        columns={isPersonalScope ? PERSONAL_COLUMNS : COMPANY_COLUMNS}
        loading={isLoading}
        emptyTitle={isPersonalScope ? 'No activity yet' : 'No company activity'}
        emptyText={
          isPersonalScope
            ? 'Your activity will appear here as you make changes.'
            : 'Company activity will appear here as members make changes.'
        }
        extraFilters={extraFilters}
        onFiltersApply={() => {/* query refetches automatically on state change */}}
        extraFiltersDirty={extraFiltersDirty}
        // Clear owns the extras too. No draft/applied split here — these three ARE the
        // applied state, so resetting them to their initial values is the whole reset.
        onClearExtraFilters={() => {
          setSelectedRoles([])
          setDateFrom(undefined)
          setDateTo(undefined)
        }}
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        serverSide
        // Parity with the certificate Activity tab: a bordered card that fills the
        // page and scrolls internally via infinite scroll (replaces the old
        // pagination footer).
        hasMore={!!hasNextPage}
        loadingMore={isFetchingNextPage}
        onLoadMore={fetchNextPage}
        fillHeight
        headerActions={
          <ActivityDownload
            onExport={handleExport}
            disabled={isExporting || items.length === 0}
          />
        }
      />
    </Stack>
  )
}
